import { computed, ref } from 'vue';
import type { PageSchema } from '@shopexperts/shared';
import { useCreateSchema } from './useCreateSchema';
import { usePageSchemas } from './usePageSchemas';

export function slugifySchemaName(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Backs the create-schema dialog wherever it appears. New schemas start with a
 * single title field so the page editor has something to edit right away.
 */
export function useCreateSchemaFlow() {
  const { schemas, refetch: refetchSchemas } = usePageSchemas();
  const { mutateAsync: createSchema, isPending: isCreating } = useCreateSchema();

  const open = ref(false);
  const name = ref('');
  const label = ref('');
  const description = ref('');
  const nameTouched = ref(false);

  const canCreate = computed(() => Boolean(name.value.trim() && label.value.trim()));

  function openDialog() {
    name.value = '';
    label.value = '';
    description.value = '';
    nameTouched.value = false;
    open.value = true;
  }

  /** The handle tracks the label until the user edits it directly. */
  function updateLabel(value: string) {
    label.value = value;
    if (!nameTouched.value) name.value = slugifySchemaName(value);
  }

  function updateName(value: string) {
    nameTouched.value = true;
    name.value = slugifySchemaName(value);
  }

  function updateDescription(value: string) {
    description.value = value;
  }

  /** Resolves to the created schema id, or null when nothing was created. */
  async function submit(): Promise<number | null> {
    if (!canCreate.value || isCreating.value) return null;

    const submittedName = name.value.trim();

    const created = await createSchema({
      name: submittedName,
      label: label.value.trim(),
      description: description.value.trim() || null,
      fields: [{
        key: 'title',
        label: 'Title',
        type: 'text',
        required: true,
        default_value: '',
        options: [],
      }],
      searchability: 'non-searchable',
    });

    open.value = false;
    await refetchSchemas();

    if (typeof created?.id === 'number') return created.id;
    return schemas.value.find((candidate: PageSchema) => candidate.name === submittedName)?.id ?? null;
  }

  return {
    open,
    name,
    label,
    description,
    canCreate,
    isCreating,
    openDialog,
    updateName,
    updateLabel,
    updateDescription,
    submit,
  };
}
